/**
 * Hooks do explorador de auditoria (A5 revamp · Brief 5 FE Tela 6 · #310).
 *
 * - `useAuditEvents`   → GET /v1/audit-events (lista + filtros + cursor).
 * - `useAuditTimeline` → GET /v1/audit-events/timeline (buckets agregados).
 * - `useAuditEvent`    → GET /v1/audit-events/:id (detalhe; payload mascarado D-B).
 * - `useAuditCascade`  → GET /v1/audit-events/cascade/:entityId (cadeia LGPD ADR-107).
 *
 * Cache keys canonical: `['admin', 'audit', ...]`.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { api } from '../../../../lib/api-client';

import type {
  AuditCascadeResultWire,
  AuditEventWire,
  AuditListFilters,
  AuditListResultWire,
  AuditTimelineFilters,
  AuditTimelineResultWire,
} from '../types';

export const AUDIT_QUERY_ROOT = ['admin', 'audit'] as const;

/**
 * Lista paginada por cursor. `filters` NÃO deve conter `cursor` (gerido pelo
 * pageParam). Use `data.pages.flatMap(p => p.items)` para a lista acumulada.
 */
export function useAuditEvents(
  filters: AuditListFilters,
  enabled = true,
): ReturnType<
  typeof useInfiniteQuery<
    AuditListResultWire,
    Error,
    { pages: AuditListResultWire[]; pageParams: unknown[] },
    readonly unknown[],
    string | undefined
  >
> {
  return useInfiniteQuery({
    queryKey: [...AUDIT_QUERY_ROOT, 'list', filters],
    queryFn: ({ pageParam }) =>
      api.listAuditEvents({ ...filters, ...(pageParam !== undefined && { cursor: pageParam }) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    staleTime: 15_000,
  });
}

export function useAuditTimeline(
  filters: AuditTimelineFilters,
  enabled = true,
): ReturnType<typeof useQuery<AuditTimelineResultWire>> {
  return useQuery({
    queryKey: [...AUDIT_QUERY_ROOT, 'timeline', filters],
    queryFn: () => api.getAuditTimeline(filters),
    enabled,
    staleTime: 15_000,
  });
}

export function useAuditEvent(id: string | null): ReturnType<typeof useQuery<AuditEventWire>> {
  return useQuery({
    queryKey: [...AUDIT_QUERY_ROOT, 'detail', id],
    queryFn: () => api.getAuditEvent(id!),
    enabled: id !== null,
  });
}

export function useAuditCascade(
  entityId: string | null,
): ReturnType<typeof useQuery<AuditCascadeResultWire>> {
  return useQuery({
    queryKey: [...AUDIT_QUERY_ROOT, 'cascade', entityId],
    queryFn: () => api.getAuditCascadeChain(entityId!),
    enabled: entityId !== null,
  });
}
